import { Plus, RefreshCw, FileText, Trash2 } from "lucide-react";

export default function MembersTab({ ctx }) {
  const {
    membersSummary, memberStatusFilter, setMemberStatusFilter, setAddingMember,
    divisions, refreshMembers, loading, openMemberProfile, memberAction, filteredMembers,
  } = ctx;
  return (
    <>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-white">Members</h1>
        <div className="flex items-center gap-2">
          <button data-testid="add-member-btn" onClick={() => setAddingMember({ player_name: "", parent_name: "", email: "", phone: "", division_id: divisions[0]?.id || "" })}
            className="inline-flex items-center gap-2 text-sm font-bold text-white bg-[#2563EB] hover:bg-[#1d4fd7] rounded-full px-5 py-2.5 transition-colors">
            <Plus size={16} /> Add Member
          </button>
          <button data-testid="members-refresh-btn" onClick={refreshMembers} disabled={loading}
            className="inline-flex items-center gap-2 text-sm font-bold text-white/70 hover:text-white border border-white/15 rounded-full px-4 py-2.5 transition-colors">
            <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-6 gap-3">
        {[["total", "Total"], ["active", "Active"], ["pending", "Pending"], ["past_due", "Past Due"], ["paused", "Paused"], ["canceled", "Canceled"]].map(([k, label]) => (
          <div key={k} className="rounded-2xl bg-white/5 border border-white/10 p-4">
            <p className="text-2xl font-extrabold text-white">{membersSummary[k] ?? 0}</p>
            <p className="text-xs font-bold uppercase tracking-wider text-white/40 mt-1">{label}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        {[["", "All"], ["active", "Active"], ["pending", "Pending"], ["past_due", "Past Due"], ["cancel_scheduled", "Cancel Scheduled"], ["paused", "Paused"], ["canceled", "Canceled"]].map(([val, label]) => (
          <button key={val || "all"} data-testid={`member-filter-${val || "all"}`} onClick={() => setMemberStatusFilter(val)}
            className={`rounded-full px-4 py-2 text-xs font-bold border transition-colors ${memberStatusFilter === val ? "border-[#2563EB] bg-[#2563EB] text-white" : "border-white/15 text-white/60 hover:text-white"}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="mt-5 space-y-3">
        {filteredMembers.length === 0 && (
          <p className="text-white/40 font-medium">No members yet. Members appear here automatically after a successful subscription.</p>
        )}
        {filteredMembers.map((m) => (
          <div key={m.id} data-testid={`admin-member-${m.id}`} className="rounded-2xl bg-white/5 border border-white/10 p-5 flex flex-wrap items-center justify-between gap-4">
            <div className="min-w-[200px] cursor-pointer" onClick={() => openMemberProfile(m.id)}>
              <div className="flex items-center gap-3">
                <span className={`inline-block rounded-full px-3 py-1 text-xs font-bold uppercase ${
                  m.status === "active" ? "bg-emerald-500/20 text-emerald-300"
                  : m.status === "pending" ? "bg-white/10 text-white/50"
                  : m.status === "past_due" ? "bg-amber-500/20 text-amber-300"
                  : m.status === "cancel_scheduled" ? "bg-orange-500/20 text-orange-300"
                  : m.status === "paused" ? "bg-sky-500/20 text-sky-300"
                  : "bg-red-500/20 text-red-300"}`}>
                  {m.status?.replace("_", " ")}
                </span>
                <p className="text-lg font-bold text-white hover:text-[#60a5fa] transition-colors">{m.player_name}</p>
              </div>
              <p className="mt-2 text-sm font-medium text-white/50">
                {m.division_name} · {m.parent_name} · {m.email}
                {m.monthly_price ? ` · $${m.monthly_price}/mo` : ""}
                {m.current_period_end ? ` · renews ${new Date(m.current_period_end).toLocaleDateString()}` : ""}
                {m.source === "manual" ? " · manual" : ""}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button data-testid={`member-profile-${m.id}`} onClick={() => openMemberProfile(m.id)} className="inline-flex items-center gap-1.5 text-xs font-bold text-white bg-[#2563EB] hover:bg-[#1d4fd7] rounded-full px-3 py-2 transition-colors"><FileText size={13} /> Profile</button>
              {m.stripe_customer_id && (
                <button data-testid={`member-portal-${m.id}`} onClick={() => memberAction(m.id, "portal")} className="text-xs font-bold text-white/70 hover:text-white border border-white/15 rounded-full px-3 py-2">Billing</button>
              )}
              {(m.status === "active" || m.status === "past_due") && (
                <button data-testid={`member-cancel-${m.id}`} onClick={() => memberAction(m.id, "cancel")} className="text-xs font-bold text-orange-300 hover:text-orange-200 border border-white/15 rounded-full px-3 py-2">Cancel</button>
              )}
              {(m.status === "cancel_scheduled" || m.status === "canceled") && (
                <button data-testid={`member-reactivate-${m.id}`} onClick={() => memberAction(m.id, "reactivate")} className="text-xs font-bold text-emerald-300 hover:text-emerald-200 border border-white/15 rounded-full px-3 py-2">Reactivate</button>
              )}
              {m.stripe_customer_id && (
                <button data-testid={`member-refund-${m.id}`} onClick={() => memberAction(m.id, "refund")} className="text-xs font-bold text-white/70 hover:text-white border border-white/15 rounded-full px-3 py-2">Refund</button>
              )}
              <button data-testid={`member-delete-${m.id}`} onClick={() => memberAction(m.id, "delete")} className="text-xs font-bold text-red-300 hover:text-red-200 border border-white/15 rounded-full px-3 py-2"><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
